import { useState, useEffect } from 'react'
import {
  Route,
  Redirect,
  Switch,
  BrowserRouter as Router
} from 'react-router-dom'
import ScrollToTop from './ScrollToTop'
import { Homepage } from './Homepage'
import { Profiles } from './Profiles'
import Faqs from './Faqs'
import About from './About'
import Technical from './TechnicalAssistance'
import { ProfileHistory } from './ProfileHistory'
import { FixitApp } from './FixitApp'
import { CertificateOfAchievementApp } from './CertificateOfAchievementApp'
import { PostIncidentSummary } from './Summary/PostIncidentSummary'
import { HistoricalIncidentSummary } from './Summary/HistoricalIncidentSummary'
import { HistoricalAwardSummary } from './Summary/HistoricalAwardSummary'
import TermsOfService from './TOS'
import Privacy from './Privacy'
import { UserSettings } from './UserSettings'
import { useLocalisationService } from 'providers/LocalisationProvider'
import FadeLoader from 'react-spinners/FadeLoader'
import { FixitGreen } from 'fixit-shared/Consts'
import { deleteCache } from 'hooks/useCache'
import { useAccountMigration } from 'hooks/useAccount'
import { CertificateOfAchievementSummary } from './Summary/CertificateOfAchievementSummary'

const HOMEPAGE = '/'
const ABOUT = '/about'
const FAQ = '/faq'
const TOS = '/tos'
const PRIVACY = '/privacy'
const TECHNICAL = '/technical-assistance'

// Module scope on purpose. Defined inside App, this was a new function identity
// on every render, and React compares element *types* by identity — so every
// authenticated page was unmounted and remounted rather than updated whenever
// App re-rendered, discarding component state and re-running every effect.
//
// `isAuthenticated` is a prop for the same reason: taking it from a closure is
// what tied this component to a particular render of App.
const AuthenticatedRoute = ({
  component: Component,
  authService,
  isAuthenticated,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={props => {
        rest.updatePage(rest.path)
        return isAuthenticated ? (
          <Component authService={authService} {...rest} {...props} />
        ) : (
          <Redirect to={{ pathname: '/', state: { from: props.location } }} />
        )
      }}
    />
  )
}

export const App = ({
  telemetryService,
  authenticationService,
  cloudFunctions
}) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const localisationService = useLocalisationService()

  useAccountMigration(cloudFunctions)

  useEffect(() => {
    const onNewUser = user_uuid => {
      const params = {
        user_uuid,
        language: localisationService.getCurrentLanguage()
        // school_id
      }
      telemetryService.reportEvent('account.created_user', params)
    }
    authenticationService.addNewUserCallback(onNewUser)

    return () => {
      authenticationService.removeNewUserCallback(onNewUser)
    }
  }, [authenticationService, localisationService, telemetryService])

  useEffect(() => {
    return deleteCache
  }, [])

  useEffect(() => {
    const onLoggedIn = () => {
      setIsAuthenticated(true)
      setIsAuthenticating(false)
    }

    const onLoggedOut = () => {
      deleteCache()
      setIsAuthenticated(false)
      setIsAuthenticating(false)
    }

    authenticationService.addAuthCallbacks(onLoggedIn, onLoggedOut)

    return () => authenticationService.removeAuthCallbacks(onLoggedIn, onLoggedOut)

  }, [authenticationService])

  let _currentPage = '/'
  let _lastPageTransition = Date.now()

  const renderHomepage = props => {
    updatePage(HOMEPAGE)
    return (
      <Homepage
        {...props}
        authService={authenticationService}
        telemetryService={telemetryService}
      />
    )
  }

  const authenticatedComponent = (path, component) => {
    return (
      <AuthenticatedRoute
        updatePage={updatePage}
        path={path}
        authService={authenticationService}
        telemetryService={telemetryService}
        cloudFunctions={cloudFunctions}
        component={component}
        isAuthenticated={isAuthenticated}
      />
    )
  }

  const renderAbout = () => {
    updatePage(ABOUT)
    return (
      <About
        authService={authenticationService}
        telemetryService={telemetryService}
      />
    )
  }

  const renderTOS = () => {
    updatePage(TOS)
    return <TermsOfService telemetryService={telemetryService} />
  }

  const renderPrivacy = () => {
    updatePage(PRIVACY)
    return <Privacy telemetryService={telemetryService} />
  }

  const renderFaqs = () => {
    updatePage(FAQ)
    return (
      <Faqs
        authService={authenticationService}
        telemetryService={telemetryService}
      />
    )
  }

  const renderTechnical = () => {
    updatePage(TECHNICAL)
    return (
      <Technical
        authService={authenticationService}
        telemetryService={telemetryService}
      />
    )
  }

  const updatePage = newPage => {
    if (newPage === _currentPage) {
      return
    }
    const telemetryProps = {
      from_page: _currentPage,
      to_page: newPage,
      time_on_page: Date.now() - _lastPageTransition
      // button_id
    }

    telemetryService.reportEvent('navigation.change_page', telemetryProps)
    _currentPage = newPage
    _lastPageTransition = Date.now()
  }

  if (isAuthenticating) {
    return (
      <div className='spinner flexbox'>
        <FadeLoader color={FixitGreen} loading={isAuthenticating} />
      </div>
    )
  }

  return (
    <Router>
      <ScrollToTop />
      <Switch>
        <Route exact path={HOMEPAGE} render={renderHomepage} />
        {authenticatedComponent('/profiles', Profiles)}
        {authenticatedComponent('/profile/:profileId/incident/:incidentId', HistoricalIncidentSummary)}
        {authenticatedComponent('/profile/:profileId/award/:incidentId', HistoricalAwardSummary)}
        {authenticatedComponent('/profile/:profileId', ProfileHistory)}
        {authenticatedComponent('/fixit', FixitApp)}
        {authenticatedComponent('/achievement', CertificateOfAchievementApp)}
        {authenticatedComponent('/summary', PostIncidentSummary)}
        {authenticatedComponent('/certificate_of_achivement', CertificateOfAchievementSummary)}
        {authenticatedComponent('/settings', UserSettings)}
        <Route path={ABOUT} render={renderAbout} />
        <Route path={FAQ} render={renderFaqs} />
        <Route path={TECHNICAL} render={renderTechnical} />
        <Route path={TOS} render={renderTOS} />
        <Route path={PRIVACY} render={renderPrivacy} />
      </Switch>
    </Router>
  )
}
